import React, { useState, useMemo, useEffect } from 'react';
import { Input } from '../components/ui/input-simple';
import { Button } from '../components/ui/button-simple';
import { Search, MapPin, Users, Eye, AlertCircle, ChevronLeft, ChevronRight, X, Building2, Sprout, Award } from 'lucide-react';
import { municipalities } from '../data/municipalities';
import { farmersAPI } from '../services/api';
import { useFarmers } from '../hooks/useApi';

// Add animations
const styles = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fade-in-up {
    animation: fadeInUp 0.5s ease-out forwards;
  }

  .animate-fade-in-up:nth-child(1) { animation-delay: 0.1s; }
  .animate-fade-in-up:nth-child(2) { animation-delay: 0.2s; }
  .animate-fade-in-up:nth-child(3) { animation-delay: 0.3s; }
  .animate-fade-in-up:nth-child(4) { animation-delay: 0.4s; }
  .animate-fade-in-up:nth-child(5) { animation-delay: 0.5s; }
  .animate-fade-in-up:nth-child(6) { animation-delay: 0.6s; }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-slide-down {
    animation: slideDown 0.6s ease-out forwards;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .animate-fade-in {
    animation: fadeIn 0.3s ease-out forwards;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(30px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  .animate-slide-up {
    animation: slideUp 0.4s ease-out forwards;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  if (!document.head.querySelector('style[data-user-farmers-animations]')) {
    styleSheet.setAttribute('data-user-farmers-animations', 'true');
    document.head.appendChild(styleSheet);
  }
}

export function UserFarmersDirectory() {
  // Use API hook for farmers management
  const {
    farmers,
    loading,
    error,
    fetchFarmers
  } = useFarmers();

  // Local state for UI
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMunicipality, setSelectedMunicipality] = useState('all');
  const [selectedOwnership, setSelectedOwnership] = useState('all');
  const [selectedFarmer, setSelectedFarmer] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Load farmers on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchFarmers(() => farmersAPI.getAll());
      } catch (error) {
      }
    };

    loadData();
  }, [fetchFarmers]);

  // Filter farmers
  const filteredFarmers = useMemo(() => {
    return farmers.filter(farmer => {
      if (farmer.id === null || farmer.id === undefined) return false;
      
      const matchesSearch = (farmer.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (farmer.municipality || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesMunicipality = selectedMunicipality === 'all' || farmer.municipality === selectedMunicipality;
      const matchesOwnership = selectedOwnership === 'all' || (farmer.landOwnership || '').toLowerCase().includes(selectedOwnership.toLowerCase());
      
      return matchesSearch && matchesMunicipality && matchesOwnership;
    });
  }, [farmers, searchTerm, selectedMunicipality, selectedOwnership]);

  // Paginated farmers with display names assigned after filtering
  // Paginated farmers with displayName and displayAddress
  const paginatedFarmers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredFarmers.slice(startIndex, endIndex).map((farmer, idx) => ({
      ...farmer,
      displayName: `Farmer ${startIndex + idx + 1}`,
      displayAddress: farmer.municipality || 'N/A'
    }));
  }, [filteredFarmers, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredFarmers.length / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedMunicipality, selectedOwnership]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Modern Header with Gradient */}
        <div className="mb-8 bg-gradient-to-r from-amber-600 to-amber-700 rounded-2xl shadow-xl p-5 text-white animate-slide-down relative z-40">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                <Users className="h-7 w-7" />
              </div>
              <div>
                <h1 className="text-2xl font-bold mb-1">Cacao Farmers Directory</h1>
                <p className="text-amber-50 max-w-2xl text-sm">
                  Browse anonymized profiles of registered cacao farmers across Bohol
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type="text"
                placeholder="Search by farmer or municipality..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 border-gray-300 focus:border-amber-500 focus:ring-amber-500 rounded-xl"
                aria-label="Search farmers"
              />
            </div>

            {/* Municipality Filter */}
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 z-10" />
              <select
                value={selectedMunicipality}
                onChange={(e) => setSelectedMunicipality(e.target.value)}
                className="pl-10 w-full h-12 border border-gray-300 rounded-xl focus:border-amber-500 focus:ring-amber-500 bg-white appearance-none cursor-pointer"
                aria-label="Filter by municipality"
              >
                <option value="all">All Municipalities</option>
                {municipalities.map(municipality => (
                  <option key={municipality} value={municipality}>{municipality}</option>
                ))}
              </select>
            </div>

            {/* Ownership Filter */}
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5 z-10" />
              <select
                value={selectedOwnership}
                onChange={(e) => setSelectedOwnership(e.target.value)}
                className="pl-10 w-full h-12 border border-gray-300 rounded-xl focus:border-amber-500 focus:ring-amber-500 bg-white appearance-none cursor-pointer"
                aria-label="Filter by ownership type"
              >
                <option value="all">All Ownership Types</option>
                <option value="owned">Owned</option>
                <option value="rented">Rented</option>
                <option value="tenanted">Tenanted</option>
              </select>
            </div>
          </div>

          {/* Results count */}
          <div className="mt-4 flex items-center justify-between text-sm">
            <span className="text-gray-600">
              Showing <span className="font-semibold text-amber-600">{filteredFarmers.length}</span> farmers
            </span>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading farmers...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-800 font-medium">Error loading farmers</span>
            </div>
            <p className="text-red-600 mt-2">{error}</p>
            <Button 
              onClick={() => window.location.reload()}
              className="mt-4 bg-red-600 hover:bg-red-700"
            >
              Retry
            </Button>
          </div>
        )}

        {/* Farmers Grid */}
        {!loading && !error && filteredFarmers.length > 0 && (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedFarmers.map((farmer) => (
            <div key={farmer.id} className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-200 hover:border-amber-300 overflow-hidden flex flex-col animate-fade-in-up" style={{ height: '380px' }}>
              {/* Card Header with Gradient */}
              <div className="bg-gradient-to-r from-amber-50 to-amber-100 p-2.5 border-b border-amber-200 flex-shrink-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg text-amber-900 font-bold mb-1 group-hover:text-amber-700 transition-colors line-clamp-1">
                      {farmer.displayName}
                    </h3>
                    <div className="flex items-center mt-1 text-gray-700">
                      <MapPin className="h-4 w-4 mr-1 flex-shrink-0 text-amber-600" />
                      <span className="text-sm">{farmer.displayAddress}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Card Body */}
              <div className="p-2.5 flex-1 flex flex-col overflow-y-auto">
                <div className="space-y-1.5 flex-1">
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Sprout className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-gray-600 font-medium">Farm Size</span>
                    </div>
                    <span className="font-semibold text-gray-900">{farmer.farmSize} ha</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-gray-600 font-medium">Ownership</span>
                    </div>
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                      {farmer.landOwnership}
                    </span>
                  </div>
                  
                  <div className="flex items-start justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Award className="h-4 w-4 text-amber-600" />
                      <span className="text-sm text-gray-600 font-medium">Varieties</span>
                    </div>
                    <div className="flex flex-wrap gap-1 justify-end max-w-[60%]">
                      {(farmer.cacaoVarieties || []).map((variety, index) => (
                        <span key={index} className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                          {variety}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Sprout className="h-4 w-4 text-emerald-600" />
                      <span className="text-sm text-gray-600 font-medium">Trees Planted</span>
                    </div>
                    <span className="font-semibold text-gray-900">{farmer.actualNumberOfTreesPlanted || 'N/A'}</span>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-2 mt-1.5 flex-shrink-0">
                  <Button 
                    className="flex-1 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-semibold flex items-center justify-center gap-2 rounded-xl shadow-md hover:shadow-lg transition-all py-2 h-9"
                    onClick={() => setSelectedFarmer(farmer)}
                  >
                    <Eye className="h-4 w-4" />
                    View Profile
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-8 bg-white p-6 rounded-2xl shadow-lg">
            <Button
              variant="outline"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-2 px-6 rounded-xl hover:bg-amber-50 hover:border-amber-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-5 w-5" />
              Previous
            </Button>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Page</span>
              <span className="px-4 py-2 bg-gradient-to-r from-amber-600 to-amber-700 text-white font-bold rounded-xl shadow-md">
                {currentPage}
              </span>
              <span className="text-sm text-gray-600">of {totalPages}</span>
            </div>
            
            <Button
              variant="outline"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center gap-2 px-6 rounded-xl hover:bg-amber-50 hover:border-amber-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        )}
        </>
        )}

        {/* Empty State */}
        {filteredFarmers.length === 0 && !loading && !error && (
          <div className="text-center py-12">
            <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-gray-600 mb-2">No farmers found</h3>
            <p className="text-gray-500">Try adjusting your filters to see more results.</p>
          </div>
        )}

        {/* Farmer Detail Modal */}
        {selectedFarmer && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slide-up">
              <div className="p-6">
                <div className="bg-gradient-to-r from-amber-600 to-amber-700 p-6 rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                        <Users className="h-8 w-8 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white">Farmer Profile</h2>
                        <p className="text-amber-50 text-sm">Limited public information</p>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      onClick={() => setSelectedFarmer(null)}
                      className="text-white hover:bg-white/20 rounded-xl h-12 w-12 p-0 transition-all hover:scale-110"
                      aria-label="Close profile"
                    >
                      <X className="h-7 w-7" />
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-6 p-6 max-h-[70vh] overflow-y-auto">
                  {/* Basic Information */}
                  <div className="bg-gradient-to-br from-amber-50 to-white p-6 rounded-2xl border border-amber-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <Users className="h-5 w-5 text-amber-600" />
                      <h3 className="text-lg font-bold text-amber-900">Basic Information</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Name</span>
                        <p className="font-semibold text-gray-900">{selectedFarmer.displayName}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Municipality</span>
                        <p className="font-semibold text-gray-900">{selectedFarmer.municipality || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Farm Details */}
                  <div className="bg-gradient-to-br from-emerald-50 to-white p-6 rounded-2xl border border-emerald-200 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <Sprout className="h-5 w-5 text-emerald-600" />
                      <h3 className="text-lg font-bold text-emerald-900">Farm Details</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Farm Size</span>
                        <p className="font-semibold text-gray-900">{selectedFarmer.farmSize} hectares</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Land Ownership</span>
                        <p className="font-semibold text-gray-900">{selectedFarmer.landOwnership || 'N/A'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl">
                        <span className="text-sm text-gray-600">Trees Planted</span>
                        <p className="font-semibold text-gray-900">{selectedFarmer.actualNumberOfTreesPlanted || 'N/A'}</p>
                      </div>
                      <div className="bg-white p-3 rounded-xl md:col-span-2">
                        <span className="text-sm text-gray-600">Cacao Varieties</span>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {(selectedFarmer.cacaoVarieties || []).map((variety, index) => (
                            <span key={index} className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                              {variety}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
